import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { LoginStateResponse } from '../../../../shared/types/routes/auth/v2';

/**
 * Query key for `GET /api/auth/state`. Exported so callers can invalidate the login state after it changes
 * server-side — e.g. once an MFA challenge is issued or a pending social login is completed. Keyed by
 * subdomain so a subdomain landing doesn't share the bare login page's cache entry.
 */
export const getAuthStateQueryKey = (subdomain?: string) =>
  (subdomain ? ['auth-state', subdomain] : ['auth-state']);

function fetchAuthState(subdomain?: string) {
  const query = subdomain ? `?subdomain=${encodeURIComponent(subdomain)}` : '';
  return get<LoginStateResponse>(`/api/auth/state${query}`);
}

/**
 * Reads the current login phase from the server on mount: the configured OAuth buttons plus any already-in-progress
 * login state (pending social auth, issued MFA challenge). With a `subdomain`, also carries the resolved
 * subdomain org so the flow can lock to it.
 */
export default function useAuthState(subdomain?: string) {
  return useQuery<LoginStateResponse>({
    queryKey: getAuthStateQueryKey(subdomain),
    queryFn: () => fetchAuthState(subdomain),
  });
}
